// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CalendarDays, Loader2, Radio, RefreshCw, Trophy } from 'lucide-react';
import { getDivisionColor } from '@/utils/divisionColors';
import { PublicEventHeader } from '@/components/public/PublicEventHeader';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { usePublicLive, useDivisionParam, formatUpdated } from '@/hooks/usePublicLive';

const FN = 'rle-event-standings';
const NAVY = '#0a1f44';

function fmtDiff(diff: number) {
  if (diff === 0) return '0';
  return diff > 0 ? `+${diff}` : `${diff}`;
}

function fmtPct(p: number | null | undefined) {
  if (p === null || p === undefined) return '—';
  // accept either 0..1 or 0..100
  const v = p <= 1 ? p * 100 : p;
  return `${v.toFixed(1)}%`;
}

const PoolTable: React.FC<{ pool: string; standings: any[]; accent: any; slug?: string }> = ({
  pool,
  standings,
  accent,
  slug,
}) => {
  const rows = [...standings].sort(
    (a, b) => (a.rank ?? 999) - (b.rank ?? 999),
  );
  return (
    <div className="rounded-lg border bg-card" style={{ borderTop: `3px solid ${accent.border}` }}>
      <div className="border-b px-4 py-2.5">
        <h4 className="text-sm font-bold uppercase tracking-wide" style={{ color: NAVY }}>
          {pool}
        </h4>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-10 text-center">#</TableHead>
            <TableHead>Team</TableHead>
            <TableHead className="w-10 text-center">W</TableHead>
            <TableHead className="w-10 text-center">L</TableHead>
            <TableHead className="hidden w-10 text-center sm:table-cell">T</TableHead>
            <TableHead className="hidden w-10 text-center sm:table-cell">GP</TableHead>
            <TableHead className="hidden w-12 text-center sm:table-cell">PF</TableHead>
            <TableHead className="hidden w-12 text-center sm:table-cell">PA</TableHead>
            <TableHead className="w-14 text-center">Diff</TableHead>
            <TableHead className="hidden w-16 text-center sm:table-cell">Win%</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((t) => {
            const isLeader = t.rank === 1;
            return (
              <TableRow
                key={t.team_id ?? t.team}
                className={isLeader ? 'bg-amber-50/70 dark:bg-amber-950/20' : ''}
                style={isLeader ? { boxShadow: `inset 3px 0 0 ${accent.border}` } : undefined}
              >
                <TableCell className="text-center font-semibold">
                  <span className="inline-flex items-center justify-center gap-1">
                    {isLeader && <Trophy className="h-3.5 w-3.5 text-amber-500" />}
                    {t.rank}
                  </span>
                </TableCell>
                <TableCell className="font-bold text-foreground">
                  {t.registration_id || t.team_id ? (
                    <Link
                      className="underline underline-offset-2"
                      to={`/schedule/${slug}/team/${t.registration_id ?? t.team_id}`}
                    >
                      {t.team}
                    </Link>
                  ) : (
                    t.team
                  )}
                </TableCell>
                <TableCell className="text-center">{t.w ?? 0}</TableCell>
                <TableCell className="text-center">{t.l ?? 0}</TableCell>
                <TableCell className="hidden text-center sm:table-cell">{t.t ?? 0}</TableCell>
                <TableCell className="hidden text-center sm:table-cell">{t.gp ?? 0}</TableCell>
                <TableCell className="hidden text-center sm:table-cell">{t.pf ?? 0}</TableCell>
                <TableCell className="hidden text-center sm:table-cell">{t.pa ?? 0}</TableCell>
                <TableCell className="text-center font-medium tabular-nums">
                  {fmtDiff(t.diff ?? 0)}
                </TableCell>
                <TableCell className="hidden text-center sm:table-cell">
                  {fmtPct(t.win_pct)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const PublicStandingsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // Division is the primary filter and lives in the URL, so the view is shareable.
  const [divisionFilter, setDivisionFilter] = useDivisionParam();

  const { data, loading, errorCode, lastUpdated, refreshing, refresh } = usePublicLive(
    FN,
    { slug },
    { enabled: !!slug },
  );

  const divisions = useMemo(() => data?.divisions || [], [data]);
  const visibleDivisions = useMemo(
    () =>
      divisionFilter === 'all'
        ? divisions
        : divisions.filter((d: any) => d.division === divisionFilter),
    [divisions, divisionFilter],
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorCode === 'NOT_PUBLISHED') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          Standings aren't available until the schedule is published.
        </h1>
      </div>
    );
  }

  if (errorCode === 'NOT_FOUND') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Event not found.</h1>
      </div>
    );
  }

  if (errorCode || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          Could not load standings.
        </h1>
      </div>
    );
  }

  const event = data.event || {};

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <PublicEventHeader
        slug={slug ?? ''} current="standings" division={divisionFilter}
        event={event} branding={data.branding} tabs={data.tabs || []} teams={[]}
      />

      <div className="mb-6">
        <div className="mt-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <span className="text-xs text-muted-foreground">
            Updated {formatUpdated(lastUpdated)}
          </span>
        </div>

        {data.scoring && (
          <p className="mt-3 text-sm text-muted-foreground">{data.scoring}</p>
        )}
        {data.games_remaining > 0 && (
          <p className="mt-1 text-sm font-medium text-amber-600">
            {data.games_remaining} game{data.games_remaining === 1 ? '' : 's'} still
            to be played — standings update live.
          </p>
        )}
      </div>

      {/* Division filter */}
      {divisions.length > 1 && (
        <div className="mb-8 flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Division</label>
          <Select value={divisionFilter} onValueChange={setDivisionFilter}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All divisions</SelectItem>
              {divisions.map((d: any) => {
                const c = getDivisionColor(d.division);
                return (
                  <SelectItem key={d.division} value={d.division}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: c.dot }}
                      />
                      {d.division}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {visibleDivisions.length === 0 && (
        <p className="py-12 text-center text-muted-foreground">
          No standings available yet.
        </p>
      )}

      {visibleDivisions.map((d: any) => {
        const c = getDivisionColor(d.division);
        return (
          <section key={d.division} className="mb-10">
            <h2
              className="mb-4 inline-flex items-center gap-2 border-l-4 pl-3 text-xl font-bold"
              style={{ color: NAVY, borderColor: c.border }}
            >
              {d.division}
            </h2>
            <div className="grid gap-5">
              {(d.pools || []).map((p: any) => (
                <PoolTable
                  key={p.pool}
                  pool={p.pool}
                  standings={p.standings || []}
                  accent={c}
                  slug={slug}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default PublicStandingsPage;
